import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import { AUTHOR, EBOOK } from "@/data/ebookData";

export default function AuthorPage() {
  useEffect(() => {
    document.title = "Shadab Rashid | Entrepreneur, AI Educator, Founder of Flynaut";
  }, []);
  return (
    <div data-testid="author-page" className="min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-12 lg:pt-20 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4"
          >
            <div className="w-full max-w-[360px] aspect-[3/4] overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A] flex items-center justify-center">
                <span className="font-cabinet text-8xl font-bold text-[#FF4F00]" data-testid="author-portrait">SR</span>
              </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-8"
          >
            <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
              The Author
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 font-cabinet text-[#0A0A0A] leading-none">
              {AUTHOR.name}
            </h1>
            <p className="text-sm text-[#FF4F00] font-mono-plex mt-3">{AUTHOR.role}</p>

            <div className="mt-8 space-y-4">
              {AUTHOR.bio.split("\n\n").map((para, i) => (
                <p key={i} className="text-base leading-loose text-[#0A0A0A] font-plex">
                  {para}
                </p>
              ))}
            </div>

            <a
              href={AUTHOR.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 text-sm font-medium text-[#0A0A0A] border-b border-[#0A0A0A] pb-1 hover:text-[#FF4F00] hover:border-[#FF4F00] transition-colors"
              data-testid="author-website-link"
            >
              Visit Website <ExternalLink size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Book Quote */}
      <section className="border-t border-[#E5E5E5] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl"
          >
            <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
              From the book
            </span>
            <p className="text-xl md:text-2xl lg:text-3xl leading-relaxed mt-6 font-cabinet font-medium text-[#0A0A0A] tracking-tight">
              "{AUTHOR.bookQuote}"
            </p>
            <cite className="block mt-6 text-sm text-[#666666] font-mono-plex not-italic">
              {AUTHOR.name}
            </cite>
          </motion.blockquote>
        </div>
      </section>

      {/* The Series */}
      <section className="border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 lg:py-24">
          <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#666666]">
            The AI Mastery Series
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 font-cabinet text-[#0A0A0A]">
            Published Works
          </h2>
          <p className="text-base text-[#666666] mt-4 leading-relaxed font-plex max-w-xl">
            A curriculum designed to help professionals at every level develop genuine, practical fluency with artificial intelligence.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8"
          >
            <Link
              to="/read/what-ai-is-not"
              className="block card-hover bg-white p-6 md:p-8 group max-w-2xl"
              data-testid="author-book-link"
            >
              <div className="flex items-start gap-6">
                <div className="shrink-0 w-20 md:w-24 aspect-[3/4] bg-[#0A0A0A] flex flex-col justify-between p-2 md:p-3">
                  <p className="font-mono-plex text-[5px] md:text-[6px] tracking-[0.2em] uppercase text-[#FF4F00]">The AI Mastery Series</p>
                  <div>
                    <p className="font-cabinet font-bold text-[10px] md:text-xs text-white leading-none tracking-tighter">What AI Is <span className="text-[#FF4F00]">NOT</span></p>
                    <p className="font-cabinet font-bold text-[8px] md:text-[9px] text-white/80 mt-1">Shadab Rashid</p>
                    <p className="font-cabinet text-[6px] text-white/50 mt-0.5">Shifa Shaikh</p>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-mono-plex text-[10px] tracking-[0.15em] uppercase text-[#FF4F00] font-bold">
                    Book 01
                  </span>
                  <h3 className="font-cabinet font-bold text-lg mt-1 text-[#0A0A0A]">{EBOOK.title}</h3>
                  <p className="text-sm text-[#666666] mt-2 leading-relaxed font-plex">{EBOOK.subtitle}</p>
                  <div className="flex items-center gap-2 mt-4 text-[#FF4F00] text-sm font-medium">
                    Read now <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
